import { isPlainObject } from 'lodash';
import { SortByFn, SortById } from '@ardoq/pagination';
import {
  Table,
  type Column,
  type Row,
  type SortDescriptor,
  tableOperations,
  type TableEmptyStateLabels,
} from '@ardoq/table';
import { GremlinMapResult, GremlinResultType } from '../types';
import { formatValue } from '../utils';
import { dispatchAction } from '@ardoq/rxbeach';
import { showGremlinResultDetails } from '../../actions';
import styled from 'styled-components';
import { SortOrder } from '@ardoq/api-types';
import { RESULTS_PER_PAGE } from '../consts';
import { ButtonSize, TertiaryButton } from '@ardoq/button';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import { autoSizeColumnProps } from './columnProps';

const Pre = styled.pre`
  border: 0;
  padding: 0;
  margin: 0;
  background: none;
  white-space: pre-wrap;
  line-height: 1.4;
`;

const ACTIONS_COLUMN_ID = '__actions';

type CellData = { value: unknown; result: GremlinMapResult };
type RowData = Record<string, CellData>;

type GremlinResultsTableMapProps = {
  results: GremlinMapResult[];
  printable?: boolean;
  sortBy: SortByFn;
  sortById: SortById;
  sortOrder?: SortOrder;
  columnKeys?: string[];
  emptyStateLabels: TableEmptyStateLabels;
  locale: Locale;
  i18nLocale: I18nLocale;
};

type MapValueCellProps = CellData & { locale: Locale };

const MapValueCell = ({ value, locale }: MapValueCellProps) =>
  isPlainObject(value) ? (
    <Pre>{JSON.stringify(value, null, 2)} </Pre>
  ) : (
    formatValue(value as string | boolean, locale)
  );

const getMapValue = ({ value }: CellData): string =>
  isPlainObject(value) ? JSON.stringify(value) : String(value);

type ShowDetailsCellProps = CellData & WithI18nLocale;

const ShowDetailsCell = ({ result, i18nLocale }: ShowDetailsCellProps) => (
  <FlexBox justify="center" height="full" align="center">
    <TertiaryButton
      buttonSize={ButtonSize.SMALL}
      onClick={() =>
        dispatchAction(
          showGremlinResultDetails({
            result,
            resultType: GremlinResultType.MAP,
          })
        )
      }
    >
      {t(i18nLocale, 'search:gremlinResultsTable.showDetails', 'Show details')}
    </TertiaryButton>
  </FlexBox>
);

const buildColumns = (
  columnKeys: string[],
  printable: boolean,
  autoHeight: boolean,
  locale: Locale,
  i18nLocale: I18nLocale
): Column<RowData>[] => {
  // Make it flexible for small number of columns, and fixed for large number of columns
  const resolvedWidths =
    columnKeys.length < 10 ? { minWidth: 150, flex: 1 } : { width: 200 };

  const columns: Column<RowData>[] = columnKeys.map(key => ({
    id: key,
    title: key,
    cellRenderer: (cellData: CellData) => (
      <MapValueCell {...cellData} locale={locale} />
    ),
    valueGetter: getMapValue,
    autoHeight,
    ...resolvedWidths,
    ...autoSizeColumnProps,
  }));
  if (!printable) {
    columns.push({
      id: ACTIONS_COLUMN_ID,
      title: '',
      cellRenderer: (cellData: CellData) => (
        <ShowDetailsCell {...cellData} i18nLocale={i18nLocale} />
      ),
      valueGetter: () => '',
      width: 150,
      suppressSorting: true,
      suppressHeaderContextMenu: true,
      resizable: false,
    });
  }
  return columns;
};

const hasPlainObjectValue = (
  results: GremlinMapResult[],
  columnKeys: string[]
): boolean =>
  results.some(result => columnKeys.some(key => isPlainObject(result[key])));

const toRows = (
  results: GremlinMapResult[],
  columnKeys: string[]
): Row<RowData>[] =>
  results.map((result, index) => {
    const data: RowData = {};
    columnKeys.forEach(key => {
      data[key] = { value: result[key], result };
    });
    data[ACTIONS_COLUMN_ID] = { value: undefined, result };
    return { id: String(index), data };
  });

const toSortDescriptor = (
  sortById: SortById,
  sortOrder: SortOrder | undefined
): SortDescriptor | null => {
  if (!sortById || !sortOrder) return null;
  return {
    column: String(sortById),
    direction: sortOrder === SortOrder.ASC ? 'asc' : 'desc',
  };
};

const GremlinResultsTableMap = ({
  results,
  printable = false,
  sortBy,
  sortById,
  sortOrder,
  columnKeys = [],
  emptyStateLabels,
  locale,
  i18nLocale,
}: GremlinResultsTableMapProps) => {
  const hasObjectValues = hasPlainObjectValue(results, columnKeys);
  return (
    <Table<RowData>
      aria-label={t(
        i18nLocale,
        'search:gremlinResultsTable.mapAriaLabel',
        'Gremlin map results'
      )}
      key={`${hasObjectValues ? 'object' : 'plain'}`}
      rows={toRows(results, columnKeys)}
      emptyStateLabels={emptyStateLabels}
      columns={buildColumns(
        columnKeys,
        printable,
        hasObjectValues,
        locale,
        i18nLocale
      )}
      sortable={{
        descriptor: toSortDescriptor(sortById, sortOrder),
        onChange: descriptor => {
          if (descriptor) sortBy(descriptor.column);
        },
        externallySorted: true,
      }}
      height={
        hasObjectValues
          ? 'calc(100vh - 410px)'
          : `${tableOperations.getDynamicTableHeight(results.length, RESULTS_PER_PAGE, { includeBottomScrollbarHeight: true })}px`
      }
    />
  );
};

export { GremlinResultsTableMap };
